import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CurrencyInput from "../../components/CurrencyInput";
import FormattedCurrency from "../../components/FormattedCurrency";
import { formatWithSystemLocale } from "../../utils/dateUtils";

import { useNotification } from "../../contexts/NotificationContext";
import { useSubscription } from "../../hooks/useSubscription";
import { Building2Icon, Download, Plus, Upload, X, Database, Edit, Trash2, Eye, Lock } from "lucide-react";
import { getApiUrl } from "../../config/api";

// Modal component for adding new membership
const AddMembershipModal = ({ isOpen, onClose, onSubmit, form, setForm, handleChange, familyMembers, hasFeature }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Add New Membership</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Association Name *</label>
            <input 
              name="association_name" 
              value={form.association_name} 
              onChange={handleChange} 
              placeholder="e.g., American Quarter Horse Association" 
              className="border px-3 py-2 rounded w-full" 
              required
            />
          </div>
          
          {/* Family Member Selection */}
          {hasFeature('family_plan') && (
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-blue-700 mb-1">Assign to Family Member</label>
              <select
                name="family_member_id"
                value={form.family_member_id || 'primary'}
                onChange={handleChange}
                className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {familyMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Membership Title</label>
            <input 
              name="membership_title" 
              value={form.membership_title} 
              onChange={handleChange} 
              placeholder="e.g., Annual Member, Lifetime Member" 
              className="border px-3 py-2 rounded w-full" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Membership Number</label>
            <input 
              name="membership_number" 
              value={form.membership_number} 
              onChange={handleChange} 
              placeholder="e.g., MEM12345" 
              className="border px-3 py-2 rounded w-full" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
            <input 
              type="date" 
              name="start_date" 
              value={form.start_date} 
              onChange={handleChange} 
              className="border px-3 py-2 rounded w-full" 
              required
            />
            <p className="text-xs text-gray-500 mt-1">When your membership begins</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date *</label>
            <input 
              type="date" 
              name="expiration_date" 
              value={form.expiration_date} 
              onChange={handleChange} 
              className="border px-3 py-2 rounded w-full" 
              required
            />
            <p className="text-xs text-gray-500 mt-1">When your membership expires</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input 
              name="notes" 
              value={form.notes} 
              onChange={handleChange} 
              placeholder="Any additional information about this membership" 
              className="border px-3 py-2 rounded w-full" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Membership Cost</label>
            <CurrencyInput
              name="amount"
              value={form.amount}
              onChange={(val) => setForm({ ...form, amount: val })}
              locale={navigator.language || 'en-US'}
              currency="USD"
              className="border px-3 py-2 rounded w-full"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Annual or one-time cost</p>
          </div>

        </div>

        <div className="flex justify-end gap-3 mt-6">
                     <button
             onClick={onClose}
                           className="px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
           >
             Cancel
           </button>
                     <button
             onClick={onSubmit}
                           className="px-3 py-2 sm:px-4 sm:py-2 bg-[#51380d] text-white rounded-lg hover:bg-[#3d2a0a] transition-colors text-sm"
           >
             Save Membership
           </button>
        </div>
      </div>
    </div>
  );
};

// Modal component for importing memberships
const ImportMembershipsModal = ({ isOpen, onClose, familyAssociations, selectedFamilyMemberId, handleFamilyMemberChange, fetchKcirodeoMemberships, isImporting, importedMemberships, saveImportedMemberships, removeImportedMembership, clearImportForm }) => {
  if (!isOpen) return null;
  
  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Import Memberships</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <p className="text-sm text-gray-600 mb-4">
            Select association to use for importing memberships. The selected association will be used to fetch available memberships.
          </p>
          
          {/* Association Selection */}
          {Array.isArray(familyAssociations) && familyAssociations.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Association for Import</h3>
              
              {familyAssociations.length === 1 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">Using Association:</span>
                    <span className="text-sm text-blue-600 font-semibold">
                      {familyAssociations[0]?.associationName || 'Unknown Association'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">Family Member:</span>
                    <span className="text-sm text-blue-600">
                      {familyAssociations[0]?.familyMemberName || 'Current User'}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    ✓ This association will be used to fetch available memberships
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">Select Association:</span>
                    <select
                      value={selectedFamilyMemberId}
                      onChange={(e) => handleFamilyMemberChange(e.target.value)}
                      className="px-3 py-2 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      {familyAssociations.map((assoc) => (
                        <option key={assoc.id} value={assoc.id}>
                          {assoc.familyMemberName} - {assoc.associationName}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Show currently selected association details */}
                  <div className="bg-white p-3 rounded border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">Selected Association:</span>
                      <span className="text-sm text-blue-600 font-semibold">
                        {(() => {
                          const selectedAssoc = familyAssociations.find(assoc => assoc.id === selectedFamilyMemberId);
                          return selectedAssoc?.associationName || 'None selected';
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-800">Family Member:</span>
                      <span className="text-sm text-blue-600">
                        {(() => {
                          const selectedAssoc = familyAssociations.find(assoc => assoc.id === selectedFamilyMemberId);
                          return selectedAssoc?.familyMemberName || 'None selected';
                        })()}
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      ✓ This association will be used to fetch available memberships
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Show loading or no associations message */}
          {!Array.isArray(familyAssociations) && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Loading association information...
              </p>
            </div>
          )}
          
          {Array.isArray(familyAssociations) && familyAssociations.length === 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                No association credentials found. Please check your settings.
              </p>
            </div>
          )}
          

          {/* Action Buttons (label above removed) */}
          <div className="flex justify-center gap-3 mb-6">
            <button
              onClick={fetchKcirodeoMemberships}
              disabled={isImporting}
              className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-[#8b6b3d] text-white rounded-lg hover:bg-[#a17a4a] disabled:opacity-50 transition-colors text-sm"
              title="Fetch Memberships"
            >
              {isImporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 h-4 mr-2 border-b-2 border-white"></div>
                  <span className="hidden sm:inline">Fetching...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Fetch Memberships</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
              title="Cancel"
            >
              Cancel
            </button>
          </div>
          
          {/* Show message when no memberships found */}
          {Array.isArray(importedMemberships) && importedMemberships.length === 0 && !isImporting && (
            <div className="text-center py-8 text-gray-500">
              Click "Fetch Memberships" to search for available memberships.
            </div>
          )}

          {/* Found Memberships */}
          {Array.isArray(importedMemberships) && importedMemberships.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <div>
                  <h3 className="text-lg font-semibold">Found Memberships</h3>
                  <p className="text-sm text-gray-600">{importedMemberships.length} membership{importedMemberships.length !== 1 ? 's' : ''} ready to import</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={clearImportForm}
                    className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={saveImportedMemberships}
                    disabled={isImporting}
                    className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    {isImporting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 h-4 mr-2 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {importedMemberships.map((membership, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <h4 className="font-semibold text-base sm:text-lg truncate">{membership.association_name}</h4>
                          {membership.membership_number && (
                            <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              #{membership.membership_number}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600">
                          {membership.membership_title && `${membership.membership_title} • `}
                          {membership.start_date} - {membership.expiration_date}
                          {membership.amount && ` • $${membership.amount}`}
                        </p>
                        {membership.notes && (
                          <p className="text-sm text-gray-500 mt-1">{membership.notes}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removeImportedMembership(index)}
                        className="text-red-600 hover:text-red-800 ml-4 flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Memberships() {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useNotification();
  const { hasFeature, subscription, features } = useSubscription();
  const [records, setRecords] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importedMemberships, setImportedMemberships] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const [form, setForm] = useState({
    association_name: "",
    start_date: "",
    expiration_date: "",
    notes: "",
    membership_title: "",
    membership_number: "",
    amount: "",
    family_member_id: "primary"
  });

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showDisclaimerDetails, setShowDisclaimerDetails] = useState(false);
  const [viewingMembership, setViewingMembership] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Family associations state
  const [familyAssociations, setFamilyAssociations] = useState([]);
  const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingMembership, setDeletingMembership] = useState(null);
 
  useEffect(() => {
    fetchRecords();
    loadSettingsForImport(); // Pre-load settings for KCIRodeo import
  }, []);

  // Load family members when subscription data is available
  useEffect(() => {
    if (hasFeature('family_plan')) {
      loadFamilyMembers();
    }
  }, [subscription, features, hasFeature]);

  useEffect(() => {
    if (familyAssociations.length > 0 && !selectedFamilyMemberId) {
      const first = familyAssociations[0];
      setSelectedFamilyMemberId(String(first.id));
    }
  }, [familyAssociations]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/associations/memberships`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      // Ensure we always set an array
      setRecords(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch error:", err);
      // Set empty array on error to prevent filter issues
      setRecords([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.association_name.trim()) {
      showWarning("Association name is required");
      return;
    }
    if (!form.start_date) {
      showWarning("Start date is required");
      return;
    }
    if (!form.expiration_date) {
      showWarning("Expiration date is required");
      return;
    }
    
    // Validate that expiration date is after start date
    if (new Date(form.expiration_date) <= new Date(form.start_date)) {
      showWarning("Expiration date must be after start date");
      return;
    }

    try {
      await axios.post(getApiUrl(`/api/associations/membership`), form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setForm({
        association_name: "",
        start_date: "",
        expiration_date: "",
        notes: "",
        membership_title: "",
        membership_number: "",
        amount: "",
        family_member_id: "primary"
      });
      setShowAddModal(false);
      fetchRecords();
      showSuccess("Membership added successfully!");
    } catch (err) {
      console.error("Submit error:", err);
      showError("Failed to add membership");
    }
  };

  const startEdit = (rec) => {
    setEditingId(rec.id);
    setEditData({
      association_name: rec.association_name,
      membership_title: rec.membership_title || "",
      membership_number: rec.membership_number || "",
      start_date: rec.start_date,
      expiration_date: rec.expiration_date,
      notes: rec.notes || "",
      amount: rec.amount || "",
      family_member_id: rec.family_member_id || "primary"
    });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(getApiUrl(`/api/associations/membership/${id}`), editData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setEditingId(null);
      setEditData({});
      fetchRecords();
      showSuccess("Membership updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      showError("Failed to update membership");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(getApiUrl(`/api/associations/membership/${id}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      fetchRecords();
      showSuccess("Membership deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      showError("Failed to delete membership");
    }
  };

  const confirmDelete = (membership) => {
    setDeletingMembership(membership);
    setShowDeleteConfirm(true);
  };

  const viewMembership = (membership) => {
    setViewingMembership(membership);
    setShowViewModal(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      ["Association Name", "Membership Title", "Membership Number", "Start Date", "Expiration Date", "Notes", "Amount", "Source"],
      ...records.map(rec => [
        rec.association_name,
        rec.membership_title || "",
        rec.membership_number || "",
        rec.start_date,
        rec.expiration_date,
        rec.notes || "",
        rec.amount || "",
        rec.source
      ])
    ].map(row => row.map(field => `"${field}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memberships_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const loadSettingsForImport = async () => {
    try {
      const res = await axios.get(getApiUrl('/api/user-associations'));
      
      if (Array.isArray(res.data)) {
        const mapped = res.data.map((assoc, index) => ({
          id: String(assoc.id),
          familyMemberId: assoc.family_member_id,  // family member ID (null for current user)
          familyMemberName: assoc.name || '',
          associationKey: assoc.association_key || '',
          associationName: assoc.association_name || '',
          userId: assoc.user_id_at_association || '',
          userPassword: assoc.user_password_at_association || ''
        }));
        setFamilyAssociations(mapped);
        setSelectedFamilyMemberId(mapped[0]?.id ?? '');
      } else {
        setFamilyAssociations([]);
      }
    } catch (err) {
      console.error('Failed to load associations for import:', err);
      setFamilyAssociations([]);
    }
  };

  const handleFamilyMemberChange = (value) => {
    setSelectedFamilyMemberId(value);
  };

  const loadFamilyMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const [userRes, familyRes] = await Promise.all([
        axios.get(getApiUrl('/api/profile'), { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(getApiUrl('/api/settings/family-members'), { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const currentUser = userRes.data;
      const familyMembers = familyRes.data || [];
            
      // Create family members list with current user first
      const members = [
        { id: 'primary', name: currentUser.name || 'Primary User', isCurrentUser: true },
        ...familyMembers.map(member => ({
          id: member.id,
          name: member.name,
          isCurrentUser: false
        }))
      ];
      
      setFamilyMembers(members);
    } catch (error) {
      console.error('Error loading family members:', error);
    }
  };

  const fetchKcirodeoMemberships = async () => {
    setIsImporting(true);
    try {
      // Get the selected association data
      const selectedAssoc = familyAssociations.find(assoc => assoc.id === selectedFamilyMemberId);
      
      if (!selectedAssoc) {
        throw new Error('No association found for the selected family member');
      }

      const res = await axios.get(getApiUrl('/api/associations/kcirodeo/memberships'), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { 
          associationKey: selectedAssoc.associationKey,
          memberKey: selectedAssoc.userId,
          memberPass: selectedAssoc.userPassword
        }
      });
      
      
      if (Array.isArray(res.data)) {
        setImportedMemberships(res.data);
        if (res.data.length === 0) {
          showWarning("No memberships found for the selected family member.");
        } else {
          showSuccess(`Found ${res.data.length} membership${res.data.length !== 1 ? 's' : ''} to import.`);
        }
      } else {
        console.error('API returned non-array data:', res.data);
        setImportedMemberships([]);
        showError("Invalid response format from server. Please try again.");
      }
    } catch (err) {
      console.error("Failed to fetch memberships:", err);
      showError("Failed to fetch memberships. Please check your association credentials.");
    } finally {
      setIsImporting(false);
    }
  };

  const saveImportedMemberships = async () => {
    setIsImporting(true);
    try {
      // Get the family member ID from the selected association
      const selectedAssoc = familyAssociations.find(assoc => assoc.id === selectedFamilyMemberId);
      const familyMemberId = selectedAssoc ? selectedAssoc.familyMemberId : null;
      
      const response = await axios.post(getApiUrl('/api/associations/kcirodeo/memberships'), {
        memberships: importedMemberships,
        familyMemberId: familyMemberId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setImportedMemberships([]);
        setShowImportModal(false);
        // Refresh the memberships list
        await fetchRecords();
        showSuccess(`Successfully imported ${response.data.length} membership${response.data.length !== 1 ? 's' : ''}!`);
      } else {
        console.error('Invalid response format from server:', response.data);
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error("Failed to save imported memberships:", err);
      showError(`Failed to import memberships: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const removeImportedMembership = (index) => {
    setImportedMemberships(prev => prev.filter((_, i) => i !== index));
  };

  const clearImportForm = () => {
    setImportedMemberships([]);
  };

  const filteredRecords = Array.isArray(records) ? records.filter(rec => {
    const matchesSearch = rec.association_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (rec.membership_title && rec.membership_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (rec.membership_number && rec.membership_number.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSource = sourceFilter === "all" || rec.source === sourceFilter;
    return matchesSearch && matchesSource;
  }) : [];

  return (
    <div className="space-y-6">
      
      
      {/* Add Membership Modal */}
      <AddMembershipModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSubmit}
        form={form}
        setForm={setForm}
        handleChange={handleChange}
        familyMembers={familyMembers}
        hasFeature={hasFeature}
      />

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-4 text-left">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0">
            <svg className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-2 sm:ml-3 flex-1 text-left">
            <p className="text-xs sm:text-sm text-yellow-800 text-left">
              <strong>Disclaimer:</strong> 
              <span className="hidden sm:inline"> Imported Membership information is provided by the originating association. Accuracy and completeness of this data are the sole responsibility of the association.</span>
              <span className="sm:hidden"> Tap for details</span>
            </p>
          </div>
          <button 
            className="sm:hidden p-1 text-yellow-600 hover:text-yellow-800 transition-colors"
            onClick={() => setShowDisclaimerDetails(!showDisclaimerDetails)}
            aria-label="Toggle disclaimer details"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {/* Mobile expandable details */}
        <div className={`sm:hidden mt-2 pt-2 border-t border-yellow-200 ${showDisclaimerDetails ? 'block' : 'hidden'}`}>
          <p className="text-xs text-yellow-800 text-left">
            Imported Membership information is provided by the originating association. Accuracy and completeness of this data are the sole responsibility of the association.
          </p>
        </div>
      </div>

      {/* Header with title and buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2Icon className="w-8 h-8 text-black-500" />
            Memberships
          </h2>
          <p className="text-sm text-gray-600 mt-1">Manage your association memberships and subscriptions</p>
        </div>
        <div className="flex flex-row gap-2 sm:gap-3 justify-center">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-[#8b6b3d] text-white rounded-lg hover:bg-[#a17a4a] transition-colors text-sm"
            title="Add New Membership"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add New Membership</span>
          </button>
          <button
            onClick={exportToCSV}
            className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-[#b97a3a] text-white rounded-lg hover:bg-[#c98540] transition-colors text-sm"
            title="Export CSV"
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Search and filter */}
      <div className="bg-white shadow p-4 rounded mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search memberships..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border px-3 py-2 rounded"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full border px-3 py-2 rounded"
            >
              <option value="all">All Sources</option>
              <option value="manual">Manual</option>
              <option value="kcirodeo">Imported</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">Memberships ({filteredRecords.length})</h3>
        {filteredRecords.length === 0 ? (
          <p className="text-gray-500">No memberships found.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredRecords.map((rec) => (
              <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                {editingId === rec.id ? (
                  <div className="w-full space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Association Name *</label>
                      <input 
                        className="border px-2 py-1 rounded w-full" 
                        name="association_name" 
                        value={editData.association_name} 
                        onChange={(e) => setEditData({ ...editData, association_name: e.target.value })} 
                        placeholder="e.g., American Quarter Horse Association" 
                      />
                    </div>
                    
                    {/* Family Member Selection */}
                    {hasFeature('family_plan') && (
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Assign to Family Member</label>
                        <select
                          className="w-full border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          name="family_member_id"
                          value={editData.family_member_id || 'primary'}
                          onChange={(e) => setEditData({ ...editData, family_member_id: e.target.value })}
                        >
                          {familyMembers.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Membership Title</label>
                      <input 
                        className="border px-2 py-1 rounded w-full" 
                        name="membership_title" 
                        value={editData.membership_title} 
                        onChange={(e) => setEditData({ ...editData, membership_title: e.target.value })} 
                        placeholder="e.g., Annual Member, Lifetime Member" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Membership Number</label>
                      <input 
                        className="border px-2 py-1 rounded w-full" 
                        name="membership_number" 
                        value={editData.membership_number} 
                        onChange={(e) => setEditData({ ...editData, membership_number: e.target.value })} 
                        placeholder="e.g., MEM12345" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                      <input 
                        type="date" 
                        className="border px-2 py-1 rounded w-full" 
                        name="start_date" 
                        value={editData.start_date} 
                        onChange={(e) => setEditData({ ...editData, start_date: e.target.value })} 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date *</label>
                      <input 
                        type="date" 
                        className="border px-2 py-1 rounded w-full" 
                        name="expiration_date" 
                        value={editData.expiration_date} 
                        onChange={(e) => setEditData({ ...editData, expiration_date: e.target.value })} 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                      <input 
                        className="border px-2 py-1 rounded w-full" 
                        name="notes" 
                        value={editData.notes} 
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })} 
                        placeholder="Any additional information" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Membership Cost</label>
                      <CurrencyInput 
                        name="amount" 
                        value={editData.amount} 
                        onChange={(val) => setEditData({ ...editData, amount: val })} 
                        locale={navigator.language || 'en-US'} 
                        currency="USD" 
                        className="border px-2 py-1 rounded w-full" 
                        placeholder="0.00"
                      />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button onClick={() => saveEdit(rec.id)} className="bg-green-600 text-white px-4 py-1 rounded-lg hover:bg-green-700 text-sm">Save</button>
                      <button onClick={cancelEdit} className="bg-gray-300 text-black px-4 py-1 rounded-lg hover:bg-gray-400 text-sm">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{rec.association_name}</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rec.source === 'kcirodeo' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {rec.source === 'kcirodeo' ? 'Imported' : 'Manual'}
                        </span>
                      </div>
                      {/* Show ownership if different from current user */}
                      {rec.owner_name && (
                        <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                          {rec.owner_name}
                        </div>
                      )}
                      <p className="text-sm text-gray-600">{rec.membership_title}</p>
                      {rec.membership_number && <p className="text-sm text-gray-600">Membership #: {rec.membership_number}</p>}
                      <p className="text-sm text-gray-600">Start: {formatWithSystemLocale(rec.start_date)} — Expires: {formatWithSystemLocale(rec.expiration_date)}</p>
                      {rec.notes && <p className="hidden sm:block text-sm italic text-gray-500">Notes: {rec.notes}</p>}
                      {rec.amount && (
                        <p className="text-sm italic text-gray-500">
                          Amount: <FormattedCurrency value={rec.amount} />
                        </p>
                      )}

                    </div>
                    {/* Mobile: Icon-only buttons */}
                    <div className="sm:hidden flex justify-center gap-2 w-full">
                      {rec.source === 'kcirodeo' ? (
                        <button 
                          onClick={() => viewMembership(rec)} 
                          className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200"
                          aria-label="View"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      ) : (
                        <button 
                          onClick={() => startEdit(rec)} 
                          className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => confirmDelete(rec)} 
                        className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Desktop: Text-based buttons */}
                    <div className="hidden sm:flex flex-wrap items-center gap-1 sm:gap-2">
                      {rec.source === 'kcirodeo' ? (
                        <button
                          onClick={() => viewMembership(rec)}
                          className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                          title="View Details"
                        >
                          View
                        </button>
                      ) : (
                        <button
                          onClick={() => startEdit(rec)}
                          className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                          title="Edit"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => confirmDelete(rec)}
                        className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Import Memberships Modal */}
      <ImportMembershipsModal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          if (importedMemberships.length > 0) {
            const count = importedMemberships.length;
            setImportedMemberships([]);
          }
        }}
        familyAssociations={familyAssociations}
        selectedFamilyMemberId={selectedFamilyMemberId}
        handleFamilyMemberChange={handleFamilyMemberChange}
        fetchKcirodeoMemberships={fetchKcirodeoMemberships}
        isImporting={isImporting}
        importedMemberships={importedMemberships}
        saveImportedMemberships={saveImportedMemberships}
        removeImportedMembership={removeImportedMembership}
        clearImportForm={clearImportForm}
      />

      {/* View Membership Modal */}
      {showViewModal && viewingMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">View Membership Details</h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingMembership(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Association Name</label>
                  <p className="text-gray-900">{viewingMembership.association_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Membership Title</label>
                  <p className="text-gray-900">{viewingMembership.membership_title || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Membership Number</label>
                  <p className="text-gray-900">{viewingMembership.membership_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <p className="text-gray-900">{formatWithSystemLocale(viewingMembership.start_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                  <p className="text-gray-900">{formatWithSystemLocale(viewingMembership.expiration_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Membership Cost</label>
                  <p className="text-gray-900">
                    {viewingMembership.amount ? (
                      <FormattedCurrency value={viewingMembership.amount} />
                    ) : 'N/A'}
                  </p>
                </div>
              </div>
              
              {viewingMembership.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <p className="text-gray-900">{viewingMembership.notes}</p>
                </div>
              )}



              <div className="flex items-center gap-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  viewingMembership.source === 'kcirodeo' 
                    ? 'bg-purple-100 text-purple-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {viewingMembership.source === 'kcirodeo' ? 'Imported' : 'Manual'}
                </span>
                {viewingMembership.owner_name && (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    {viewingMembership.owner_name}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingMembership(null);
                }}
                className="bg-gray-300 text-black px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Membership</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete <strong>"{deletingMembership.association_name}"</strong> membership?
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingMembership(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deletingMembership) {
                    handleDelete(deletingMembership.id);
                  }
                  setShowDeleteConfirm(false);
                  setDeletingMembership(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
